import { Center, Circle, HStack, Spinner, Stack, Text } from '@chakra-ui/react';
import { Navigate, useNavigate } from 'react-router-dom';

import ErrorAlert from '@/client/components/data-display/ErrorAlert';
import LearnerDashboardNoDataPlaceholder from '@/client/components/learners/dashboard/components/LearnerDashboardNoDataPlaceholder';
import RecommendationsService from '@/client/services/api/recommendations/RecommendationsService';
import { Sparkles } from '@/client/components/icons/ContinuIcons';
import StackedLearnerDashboardContentListItem from '../components/lists/StackedLearnerDashboardContentListItem';
import { setContentUrl } from '@/client/components/learners/dashboard/utils/setContentUrl';
import { useAccessHelper } from '@/client/services/hooks/auth/useAccessHelper';
import { useLearnerDashboard } from '@/client/components/learners/dashboard/hooks/useLearnerDashboard';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

export default function LearnerDashboardRecommendations() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { allowance } = useAccessHelper();
  const { recommendations } = allowance;
  const { contentTypeTranslationMap } = useLearnerDashboard();

  if (!recommendations) {
    return <Navigate to="/dashboard" replace />;
  }

  const { data, isLoading, isError } = useQuery({
    enabled: recommendations,
    queryKey: ['general-explore-recommendations'],
    queryFn: () => RecommendationsService.getGeneralRecommendations(),
  });

  if (isLoading) {
    return (
      <Center marginTop={10}>
        <Spinner />
      </Center>
    );
  }

  if (isError) {
    return (
      <ErrorAlert
        title={t('learners.dashboard.recommendations.error')}
        backAction={{
          to: '/dashboard',
          label: t('learners.dashboard.recommendations.error.backToDashboard'),
        }}
      />
    );
  }

  return (
    <>
      <HStack
        spacing={1}
        position="sticky"
        top={0}
        backgroundColor="white"
        zIndex={1}
        paddingBottom={2}
      >
        <Circle size="20px" backgroundColor="neutral.100">
          <Sparkles boxSize={3} />
        </Circle>

        <Text fontSize="16px" fontWeight={400} color="neutral.800">
          {t('learners.dashboard.recommendations.subTitle')}
        </Text>
      </HStack>

      {data.content.length === 0 && (
        <LearnerDashboardNoDataPlaceholder
          imagePath="/public/images/Hiring-1--Streamline-Brooklyn.svg"
          title={t('learners.dashboard.browse.noRecommendations')}
          helpText={t('learners.dashboard.levelUp')}
          buttonData={{
            buttonCopy: t('learners.dashboard.toExplore'),
            buttonAction: () => navigate('/explore'),
          }}
        />
      )}

      <Stack spacing={0} paddingLeft={6} marginTop={4}>
        {data?.content.map((content, index) => {
          const imageUrl = content.images?.explore || content.image;

          return (
            <StackedLearnerDashboardContentListItem
              key={content._id}
              id={content._id}
              title={content.title || content.name}
              formattedContentDuration=""
              contentType={contentTypeTranslationMap[content.type]}
              imageUrl={imageUrl}
              onClick={() => navigate(setContentUrl(content._id, content.type))}
            />
          );
        })}
      </Stack>
    </>
  );
}
